
import { Component, Emit, Prop, Vue } from "vue-property-decorator";
import TimeUtil from "@/lib/common/time-util";
import { Check } from "@/lib/enum/check";
import { PsycGaugeConfig } from "@/lib/model/exambuilder/exam-v2";
import ExamV3Custom, {
  ExamV3Tem
} from "@/lib/model/exambuilder/exam-v3/exam-v3";
import { PsyUse } from "@/lib/model/exambuilder/exam-v3/exam-v3-sub";
import CircleProgress from "@/views/ai-builder-v3/components/circle-progress.vue";
import EllipShow from "@/components/ellipShow.vue";

const noSelectionText = "待选择";

@Component({ components: { EllipShow, CircleProgress } })
export default class InvestigateTree extends Vue {
  @Prop({ type: String, default: "select" })
  mode!: "select" | "score"; // 选题模式：select，配分模式：score

  @Prop({ type: Object, default: null })
  exam!: ExamV3Custom | ExamV3Tem;

  @Prop({ type: Array, default: () => [] })
  mindList!: PsycGaugeConfig[];

  @Prop({ type: Number })
  activeType!: Check;

  @Prop({ type: String })
  activeItem?: string;

  // tooltip展示控制
  hideTooltipFlag = false;

  get basicQualityList() {
    return this.exam.processInformation.basicQualityList.filter(
      item => item.dimensionKey !== ""
    );
  }

  get objectiveQuestionScoreList() {
    return this.exam.processInformation.objectiveQuestionScoreList.filter(
      item => item.questionId !== 0
    );
  }

  get knowledgeAndSkills() {
    return this.exam.processInformation.knowledgeAndSkills;
  }

  get physicalInstrument() {
    return this.exam.processInformation.physicalInstrument;
  }

  get jobMatching() {
    return this.exam.processInformation.jobMatching;
  }

  get psychologicalMotivation() {
    return this.exam.processInformation.psychologicalMotivation;
  }

  // 暴露枚举
  get Check() {
    return Check;
  }

  // 格式化用时
  formatTimeMsg(second: number, pre = "约") {
    if (!second) return noSelectionText;

    return TimeUtil.formatTimeMsg(second, pre);
  }

  // 形体检测所需时长
  get fingerTestMenuInfo() {
    const { testingCount, testingTime } = this.physicalInstrument.fingerTest;

    return {
      total: testingCount,
      time: testingTime,
      timeMsg: this.formatTimeMsg(testingTime)
    };
  }

  // 辨色检测所需时长
  get colorTestMenuInfo() {
    const { testingTime } = this.physicalInstrument.colorTest;
    return { time: testingTime, timeMsg: this.formatTimeMsg(testingTime) };
  }

  // 小游戏预计时长
  get gameMenuInfo() {
    const time = this.knowledgeAndSkills.gameTestingTime;
    return { time, timeMsg: this.formatTimeMsg(time) };
  }

  // 心理量表映射
  get psycMap() {
    const {
      psyc1,
      psyc3,
      psyc4,
      psyc5,
      psyc1TestingTime,
      psyc3TestingTime,
      psyc4TestingTime,
      psyc5TestingTime
    } = this.psychologicalMotivation;

    return {
      [Check.BIG_FIVE_240]: { psyc: psyc1, time: psyc1TestingTime },
      [Check.DISC]: { psyc: psyc3, time: psyc3TestingTime },
      [Check.BIG_FIVE_60]: { psyc: psyc4, time: psyc4TestingTime },
      [Check.DVF]: { psyc: psyc5, time: psyc5TestingTime }
    };
  }

  // 心理测评量表和预估时长
  get selectedPsycList() {
    if (!this.mindList?.length) return [];

    return this.mindList
      .filter(item => {
        return item.psycType && this.psycMap[item.psycType].psyc.use === 1;
      })
      .map(item => {
        const config: { psyc: PsyUse; time: number } = this.psycMap[
          item.psycType as Check
        ];

        return {
          psyc: item,
          total: config.psyc.psycGaugeSubDimensionList.reduce(
            (pre, item) => pre + item.questionCount,
            0
          ),
          timeInfo: {
            time: config.time,
            timeMsg: this.formatTimeMsg(config.time)
          }
        };
      });
  }

  // 英语题目数量、时长、配分
  get iseEnglishMenuInfo() {
    const {
      testingTime,
      questions,
      score
    } = this.knowledgeAndSkills.iseEnglish;

    return {
      total: questions.length,
      score: Number(score),
      timeInfo: {
        time: testingTime,
        timeMsg: this.formatTimeMsg(testingTime)
      }
    };
  }

  // 普通话题目数量、时长、配分
  get iseChineseMenuInfo() {
    const {
      testingTime,
      questions,
      score
    } = this.knowledgeAndSkills.iseChinese;

    return {
      total: questions.length,
      score: Number(score),
      timeInfo: {
        time: testingTime,
        timeMsg: this.formatTimeMsg(testingTime)
      }
    };
  }

  // nlp维度和题目数量、时长、配分
  get selectedDimensionsList() {
    return this.basicQualityList
      .map(item => {
        const { questions, score, testingTime } = item;
        let dimensionScore = score;

        if (item.isCustom) {
          dimensionScore = this.objectiveQuestionScoreList.reduce(
            (pre, item) => pre + Number(item.score),
            0
          );
        }

        return {
          sort: item.sort,
          initSort: item.initSort,
          dimension: item,
          total: questions.length,
          score: dimensionScore,
          timeInfo: {
            time: testingTime,
            timeMsg: this.formatTimeMsg(testingTime)
          }
        };
      })
      .sort((a, b) => a.initSort - b.initSort)
      .sort((a, b) => Number(a.initSort === 0) - Number(b.initSort === 0));
  }

  // 选中题目总数和总预计时长
  get allQuestionsShowInfo() {
    return {
      total: this.exam.processInformation.questionCount.total,
      time: this.exam.processInformation.allTestingTime
    };
  }

  // 总分
  get totalScore() {
    const nlpScore = this.selectedDimensionsList.reduce(
      (pre, item) => pre + Number(item.score),
      0
    );

    return (
      nlpScore + this.iseEnglishMenuInfo.score + this.iseChineseMenuInfo.score
    );
  }

  // 只看打分项
  onlyShowScoreItem = false;

  @Emit()
  changeOnlyShowScore() {
    return this.onlyShowScoreItem;
  }

  // 是否展示体质仪表
  get showPhysicalInstrument() {
    const { fingerTest, colorTest } = this.physicalInstrument;

    const useFingerTest = fingerTest.use === 1;
    const useColorTest = colorTest.use === 1;

    return {
      [Check.FINGER_TEST]: useFingerTest && !this.onlyShowScoreItem,
      [Check.COLOR_TEST]: useColorTest && !this.onlyShowScoreItem,
      use: (useFingerTest || useColorTest) && !this.onlyShowScoreItem
    };
  }

  // 是否展示工作匹配
  get showJobMatching() {
    const checkIdentityInfo = this.jobMatching.identityInfo === 1;
    const checkSocialInfo = this.jobMatching.socialInfo === 1;
    const checkFinancialRisk = this.jobMatching.financialRisk === 1;

    return {
      [Check.IDENTITY_INFO]: checkIdentityInfo && !this.onlyShowScoreItem,
      [Check.SOCIAL_INFO]: checkSocialInfo && !this.onlyShowScoreItem,
      [Check.FINANCIAL_RISK]: checkFinancialRisk && !this.onlyShowScoreItem,
      use:
        (checkIdentityInfo || checkSocialInfo || checkFinancialRisk) &&
        !this.onlyShowScoreItem
    };
  }

  // 是否展示知识技能
  get showKnowledge() {
    const { iseEnglish, iseChinese, gameTests } = this.knowledgeAndSkills;

    const checkEnglish = iseEnglish.use === 1;
    const checkChinese = iseChinese.use === 1;
    const useGame = gameTests.length > 0;

    return {
      [Check.ENGLISH]: checkEnglish,
      [Check.CHINESE]: checkChinese,
      [Check.GAME]: useGame && !this.onlyShowScoreItem,
      use: checkEnglish || checkChinese || (useGame && !this.onlyShowScoreItem)
    };
  }

  // 是否展示基础素质
  get showDimensions() {
    return {
      use: !!this.basicQualityList.length
    };
  }

  // 是否展示心理动机
  get showPsyc() {
    const { psyc1, psyc3, psyc4, psyc5 } = this.psychologicalMotivation;

    return {
      [Check.BIG_FIVE_240]: psyc1.use === 1 && !this.onlyShowScoreItem,
      [Check.DISC]: psyc3.use === 1 && !this.onlyShowScoreItem,
      [Check.BIG_FIVE_60]: psyc4.use === 1 && !this.onlyShowScoreItem,
      [Check.DVF]: psyc5.use === 1 && !this.onlyShowScoreItem,
      use:
        (psyc1.use === 1 ||
          psyc3.use === 1 ||
          psyc4.use === 1 ||
          psyc5.use === 1) &&
        !this.onlyShowScoreItem
    };
  }

  // 点击考察项
  activeKey = 1;

  @Emit()
  active(activeType: Check, activeItem?: string) {
    this.activeKey++;

    return { activeType, activeItem };
  }
}
